import { GlassCard } from '@/components/design-system';
import { EmptyState } from '@/components/poof-ui';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyEvents, type EventsResponse } from '@/lib/collections/events';
import { subscribeManyListings, type ListingsResponse } from '@/lib/collections/listings';
import { subscribeUsers, type UsersResponse } from '@/lib/collections/users';
import { isPlasticType, PLASTIC_TYPE_LABELS } from '@/utils/plastic-rates';
import { useAuth } from '@pooflabs/web';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Compass, MapPin } from 'lucide-react';
import { useMemo } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';

/** Fallback center when the household has no city match nearby — Lagos, the network's home base. */
const LAGOS_CENTER: [number, number] = [6.5244, 3.3792];

function parseCoord(value: string | undefined): number | null {
  if (!value) return null;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

/** Small rounded pin with a drop tail, colored/glyphed per activity type — built as a divIcon so no external marker image assets are needed. */
function pinIcon(bg: string, glyph: string) {
  return L.divIcon({
    className: '',
    html: `
      <div style="display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 3px 6px rgba(0,0,0,0.55));">
        <div style="
          width:30px;height:30px;border-radius:9999px;background:${bg};
          display:flex;align-items:center;justify-content:center;
          box-shadow:0 0 0 3px rgba(255,255,255,0.14);
          font-size:15px;line-height:1;
        ">${glyph}</div>
        <div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:9px solid ${bg};margin-top:-2px;"></div>
      </div>
    `,
    iconSize: [30, 40],
    iconAnchor: [15, 40],
    popupAnchor: [0, -38],
  });
}

const listingIcon = pinIcon('hsl(153 85% 40%)', '♻️');
const driveIcon = pinIcon('hsl(266 80% 58%)', '🚚');

interface MapPin {
  lat: number;
  lng: number;
  type: 'listing' | 'drive';
  listing?: ListingsResponse;
  drive?: EventsResponse;
}

/**
 * Household-facing map: open recovery listings + active community drop-off
 * drives near the signed-in household's set city, on free OpenStreetMap
 * tiles via Leaflet. Falls back to a Lagos-centered view when the household
 * has no city set yet, or nothing nearby matches.
 */
export function NearbyActivityMap() {
  const { user } = useAuth();

  const { data: profile } = useRealtimeData<UsersResponse | null>(
    subscribeUsers,
    !!user?.address,
    user?.address ?? ''
  );

  const { data: listings, loading: listingsLoading } = useRealtimeData<ListingsResponse[]>(
    subscribeManyListings,
    true,
    ''
  );
  const { data: events, loading: eventsLoading } = useRealtimeData<EventsResponse[]>(
    subscribeManyEvents,
    true,
    ''
  );

  const city = profile?.city;
  const loading = listingsLoading || eventsLoading;

  const pins = useMemo<MapPin[]>(() => {
    if (!city) return [];

    const listingPins: MapPin[] = (listings ?? [])
      .filter((l) => l.status === 'open' && l.city === city)
      .map((listing): MapPin | null => {
        const lat = parseCoord(listing.lat);
        const lng = parseCoord(listing.lng);
        if (lat === null || lng === null) return null;
        return { lat, lng, type: 'listing', listing };
      })
      .filter((p): p is MapPin => p !== null);

    const drivePins: MapPin[] = (events ?? [])
      .filter((e) => e.status === 'active' && e.city === city)
      .map((drive): MapPin | null => {
        const lat = parseCoord(drive.lat);
        const lng = parseCoord(drive.lng);
        if (lat === null || lng === null) return null;
        return { lat, lng, type: 'drive', drive };
      })
      .filter((p): p is MapPin => p !== null);

    return [...listingPins, ...drivePins];
  }, [city, listings, events]);

  const center = useMemo<[number, number]>(() => {
    if (pins.length === 0) return LAGOS_CENTER;
    const avgLat = pins.reduce((sum, p) => sum + p.lat, 0) / pins.length;
    const avgLng = pins.reduce((sum, p) => sum + p.lng, 0) / pins.length;
    return [avgLat, avgLng];
  }, [pins]);

  if (!loading && pins.length === 0) {
    return (
      <GlassCard padding="lg">
        <EmptyState
          icon={<Compass className="h-6 w-6" />}
          title={city ? `Nothing nearby in ${city} yet` : 'Set your city to see nearby activity'}
          description={
            city
              ? 'No open listings or active drives in your city right now — check back soon.'
              : 'Add your city on the Wallet page and open listings + community drives near you will show up here.'
          }
        />
      </GlassCard>
    );
  }

  return (
    <GlassCard padding="sm" className="overflow-hidden">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 px-1 pt-1">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex h-2.5 w-2.5 rounded-full" style={{ background: 'hsl(153 85% 40%)' }} />
          Open listings
          <span className="ml-3 inline-flex h-2.5 w-2.5 rounded-full" style={{ background: 'hsl(266 80% 58%)' }} />
          Community drives
        </div>
        {city && (
          <span className="flex items-center gap-1 text-xs font-medium text-foreground">
            <MapPin className="h-3.5 w-3.5 text-primary" />
            {city}
          </span>
        )}
      </div>

      <div className="overflow-hidden rounded-xl" style={{ height: 440 }}>
        <MapContainer
          key={`${center[0].toFixed(4)},${center[1].toFixed(4)},${pins.length}`}
          center={center}
          zoom={pins.length > 0 ? 12 : 11}
          scrollWheelZoom
          style={{ height: '100%', width: '100%', background: 'hsl(165 18% 10%)' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {pins.map((p, i) => (
            <Marker
              key={`${p.type}-${i}`}
              position={[p.lat, p.lng]}
              icon={p.type === 'listing' ? listingIcon : driveIcon}
            >
              <Popup>
                {p.type === 'listing' && p.listing ? (
                  <div className="text-sm">
                    <strong className="block text-foreground">
                      {isPlasticType(p.listing.plasticType)
                        ? PLASTIC_TYPE_LABELS[p.listing.plasticType]
                        : p.listing.plasticType}
                    </strong>
                    <span className="block text-muted-foreground">{p.listing.estimatedKg} kg</span>
                    <span className="block text-muted-foreground">{p.listing.locationName}</span>
                  </div>
                ) : p.drive ? (
                  <div className="text-sm">
                    <strong className="block text-foreground">{p.drive.title}</strong>
                    <span className="block text-muted-foreground">Target: {p.drive.targetKg} kg</span>
                    <span className="block text-muted-foreground">{p.drive.locationName}</span>
                  </div>
                ) : null}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </GlassCard>
  );
}

export default NearbyActivityMap;
